import { QueryKeys } from "@/constants/Query";
import { useApi } from "@/hooks/useApi";
import axiosInstance from "@/lib/axios";

export enum ChatActionType {
  LIVE_CHAT = "TEXT_BASED_LIVE_CHAT",
  ASYNC_CHAT = "TEXT_BASED_ASYNC_CHAT",
  VIDEO_CALL = "AUDIO_VISUAL_CHAT",
}

export enum UnavailableReason {
  NOT_A_PROVIDER = "NOT_A_PROVIDER",
  NOT_ON_SHIFT = "NOT_ON_SHIFT",
  FEATURE_UNAVAILABLE_FOR_PARTNER = "FEATURE_UNAVAILABLE_FOR_PARTNER",
  UNAUTHORIZED_ROLE = "UNAUTHORIZED_ROLE",
  ENCOUNTER_NOT_OWNED = "ENCOUNTER_NOT_OWNED",
  OPEN_LIVE_ENCOUNTER = "OPEN_LIVE_ENCOUNTER",
  INVALID_ENCOUNTER_STATUS = "INVALID_ENCOUNTER_STATUS",
  UNAVAILABLE_FOR_ASYNC_ENCOUNTER = "UNAVAILABLE_FOR_ASYNC_ENCOUNTER",
  UNAVAILABLE_FOR_AMAZON_PATIENT = "UNAVAILABLE_FOR_AMAZON_PATIENT",
  UNAVAILABLE_FOR_UNIVERSAL_CLINIC_PATIENT = "UNAVAILABLE_FOR_UNIVERSAL_CLINIC_PATIENT",
  PATIENT_REVOKED_CONSENT = "PATIENT_REVOKED_CONSENT",
  UNAVAILABLE_FOR_UC_ASYNC_VISIT = "UNAVAILABLE_FOR_UC_ASYNC_VISIT",
  UNAVAILABLE_FOR_UC_ASYNC_DX_VISIT = "UNAVAILABLE_FOR_UC_ASYNC_DX_VISIT",
}

export const REASON_LABELS = {
  [UnavailableReason.NOT_A_PROVIDER]:
    "Your account is not classified as a provider.",
  [UnavailableReason.NOT_ON_SHIFT]: "You are not on shift.",
  [UnavailableReason.FEATURE_UNAVAILABLE_FOR_PARTNER]:
    "Chat is not enabled for this patient's sponsoring partner.",
  [UnavailableReason.UNAUTHORIZED_ROLE]:
    "You are not authorized to send messages on this type of visit.",
  [UnavailableReason.ENCOUNTER_NOT_OWNED]:
    "You must take this visit to send messages.",
  [UnavailableReason.OPEN_LIVE_ENCOUNTER]:
    "This patient has an open live visit.",
  [UnavailableReason.INVALID_ENCOUNTER_STATUS]:
    "This visit is not in a valid state to send messages.",
  [UnavailableReason.UNAVAILABLE_FOR_ASYNC_ENCOUNTER]:
    "AV calls are not allowed for this visit.",
  [UnavailableReason.UNAVAILABLE_FOR_AMAZON_PATIENT]:
    "AV calls are not allowed for this patient.",
  [UnavailableReason.UNAVAILABLE_FOR_UNIVERSAL_CLINIC_PATIENT]:
    "Chat is not available for Universal Clinic patients.",
  [UnavailableReason.PATIENT_REVOKED_CONSENT]:
    "Patient revoked consent. For important outreach, call or email them.",
  [UnavailableReason.UNAVAILABLE_FOR_UC_ASYNC_VISIT]:
    "Restricted for Async Rx visits.",
  [UnavailableReason.UNAVAILABLE_FOR_UC_ASYNC_DX_VISIT]:
    "Restricted for Async Dx visits.",
};

interface ChatAction {
  action: ChatActionType;
  unavailableReasons: UnavailableReason[];
}

interface ChatAvailableActionsResponse {
  availableActions: ChatAction[];
}

export const useChatAvailableActions = ({
  patientId,
  encounterId,
}: {
  patientId: string;
  encounterId?: string;
}) => {
  return useApi<
    ChatAvailableActionsResponse,
    {
      actions: Record<ChatActionType, UnavailableReason[]>;
      chatMode: ChatActionType;
    }
  >({
    queryOptions: {
      queryFn: async () => {
        const response = await axiosInstance.get(
          "/new-api/chat/available-actions",
          {
            params: {
              patientId,
              encounterId,
            },
          },
        );
        return response.data;
      },
      queryKey: QueryKeys.Patient.Chat.AVAILABLE_ACTIONS(
        patientId,
        encounterId,
      ),
      select: (data) => {
        const actions = data.availableActions.reduce(
          (acc, action) => {
            acc[action.action] = action.unavailableReasons;
            return acc;
          },
          {} as Record<ChatActionType, UnavailableReason[]>,
        );

        // Determine chatMode: use LIVE_CHAT if it exists in response, otherwise ASYNC_CHAT
        const chatMode =
          actions[ChatActionType.LIVE_CHAT] !== undefined
            ? ChatActionType.LIVE_CHAT
            : ChatActionType.ASYNC_CHAT;

        return { actions, chatMode };
      },
    },
  });
};
